import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ShoppingBag, Store, Truck, Info, MapPin, StickyNote, ArrowDown, ShoppingCart,
} from "lucide-react";
import {
  Auticko, AutickoLoader, Button, Checkbox, Modal, PageHero, QuantityInput, RadioButton, Select,
  UzavierkaObjednavok,
} from "../../components";
import { useAuth } from "../../context/AuthContext";
import { ITEM_TYPE_META, DAY_NAMES, DAY_SHORT_NAMES, ALLERGENS } from "../../features/orderConstants";
import { getKosik, setKosik } from "../../features/tenantStorage";
import { compareMenuCodesNatural } from "../../features/orderUtils";
import { formatZostava, terazVBratislave, useUzavierka, zajtra } from "../../features/uzavierka";
import {
  ORDER_CONFIG_URL,
  CUSTOMER_ORDERS_URL,
  CUSTOMER_PROFILE_URL,
  CUSTOMER_ADDRESS_URL,
} from "../../features/UrlHelper";
import "./ObjednavkaPage.scss";

/**
 * Objednavacia obrazovka zakaznika (/objednavka).
 *
 * ROZLOZENIE: na mobile jeden stlpec, na sirokej obrazovke ponuka vlavo a
 * SUHRN vpravo, ktory sa drzi pri skrolovani. Zakaznik tak po cely cas vidi,
 * co uz ma v kosiku a za kolko -- bez toho musel skrolovat na spodok a spat.
 *
 * MENU SA DA PREZERAT BEZ PRIHLASENIA (R41) -- prihlasenie si vypyta az
 * odoslanie. Ceny sa pritom uz prihlasenemu pocitaju podla JEHO cennika (R21),
 * o to sa stara `/api/order-config` podla tokenu v poziadavke.
 *
 * CENU POCITA SERVER. To, co sa tu scitava, je len nahlad -- pri odoslani
 * sa poslu iba ID jedal a pocty a zavazna je cena, ktoru vrati server.
 */

/**
 * Volby balenia pri odbere na prevadzke (R46). Enum sa posiela serveru.
 * `kratky` ide do zbaleneho prehladu doručenia, kde na celu vetu nie je miesto.
 */
const BALENIE = [
  { value: "NA_MIESTE", label: "Zjem to na prevádzke", kratky: "zjete to na mieste" },
  { value: "PREVADZKA", label: "Zabaľte do svojich obalov", kratky: "zabalíme to" },
  { value: "VLASTNE", label: "Prinesiem si vlastné obaly", kratky: "vo vlastných obaloch" },
];

/** Ta ista veta, ktorou objednavku po uzavierke odmietne server (DOVOD.PO_UZAVIERKE). */
const PO_UZAVIERKE = "Na dnes sa už objednať nedá, čas na objednávky vypršal.";

/** Rovnaky limit ako `customer_addresses.address_note` (VARCHAR(100)). */
const MAX_SPRESNENIE = 100;

const eur = (v) => `${Number(v || 0).toFixed(2)} €`;

/** Skloňovanie počtu položiek v hlavičke súhrnu: 1 položka, 2 položky, 5 položiek. */
const pocetSlovom = (n) => (n === 1 ? "položka" : n < 5 ? "položky" : "položiek");

function popisDna(datum, dnes) {
  const [r, m, d] = datum.split("-").map(Number);
  const den = new Date(Date.UTC(r, m - 1, d)).getUTCDay();
  const index = den === 0 ? 6 : den - 1;
  // „Dnes“ a „Zajtra“ namiesto názvu dňa: tak o dňoch človek uvažuje, „piatok
  // 10. 10.“ by si na „zajtra“ prepočítaval sám. Dátum ostáva vedľa, takže sa
  // nič nestratí; `tyzden` drží názov dňa pre čítačky.
  const relativny = datum === dnes ? "Dnes" : dnes && datum === zajtra(dnes) ? "Zajtra" : null;
  // Celý názov dňa, nie skratka: „Po“ a „Pi“ sa pri rýchlom čítaní pletú
  // a na skratku nie je dôvod, keď je miesta dosť. Na úzkom telefóne sa
  // prepne na skratku cez CSS, nie vynechaním údaja.
  return {
    nazov: relativny || DAY_NAMES[index],
    skratka: relativny || DAY_SHORT_NAMES[index],
    tyzden: relativny ? DAY_NAMES[index].toLowerCase() : null,
    cislo: `${d}. ${m}.`,
  };
}

const ALERGEN = Object.fromEntries(ALLERGENS.map((a) => [String(a.id), a]));

/** "1,3,9" → ["1", "3", "9"] — server ich drží ako text oddelený čiarkami. */
const alergeny = (zoznam) =>
  String(zoznam || "").split(",").map((a) => a.trim()).filter(Boolean);

/**
 * Z ulozeneho kosika necha len to, co sa na dany den da stale objednat --
 * medzicasom mohlo jedlo dojst alebo sa po prihlaseni zmenil cennik (R21).
 * Odosiela sa cely `pocty`, takze by inak serveru islo aj jedlo, ktore na
 * obrazovke nevidno a z kosika sa neda odobrat.
 */
function platnyKosik(pocty, config, den) {
  const ceny = config.ceny?.[den] || {};
  const platne = {};
  for (const p of config.polozky || []) {
    if (p.serving_date && p.serving_date !== den) continue;
    const ks = Number(pocty?.[p.id]);
    const cena = ceny[p.id];
    if (!Number.isInteger(ks) || ks <= 0) continue;
    if (p.nedostupne || cena?.price == null || cena.objednatelne === false) continue;
    platne[p.id] = ks;
  }
  return platne;
}

/** Jeden riadok rozpisu v súhrne: počet, kód s názvom a cena (alebo „v cene“). */
function RiadokRozpisu({ riadok: r }) {
  return (
    <li className="objednavka__rozpis-riadok">
      <span className="objednavka__rozpis-ks">{r.ks}×</span>
      <span className="objednavka__rozpis-nazov">
        {r.kod && <em className="objednavka__rozpis-kod">{r.kod}</em>}
        {r.nazov}
      </span>
      <span className="objednavka__rozpis-cena">{r.vCene ? "v cene" : eur(r.cena)}</span>
    </li>
  );
}

function ObjednavkaPage() {
  const navigate = useNavigate();
  // `/objednavka?den=YYYY-MM-DD&polozka=<id>` -- sem vedie "Objednať" pri
  // jedle v dennom menu (R41). Parametre su len navrh: ked den uz nie je
  // v okne alebo jedlo v ponuke, ticho sa ignoruju a plati predvolba.
  const [parametre] = useSearchParams();
  const zvolenyDen = parametre.get("den");
  const zvolenaPolozka = parametre.get("polozka");
  const { isLoggedIn, userRole } = useAuth();
  const jeZakaznik = isLoggedIn && userRole === "CUSTOMER";

  const [config, setConfig] = useState(null);
  const [chybaNacitania, setChybaNacitania] = useState("");
  const [den, setDen] = useState("");
  // Kosik PO DNOCH: `{ "2026-10-01": { [id jedla]: ks } }`. Jedla su viazane na
  // den, ale prepnutie dna vyber nezahodi -- clovek sa casto pozrie, co je
  // zajtra, a vrati sa. Odosiela sa vzdy len kosik zobrazeneho dna.
  const [kosiky, setKosiky] = useState({});
  const pocty = useMemo(() => kosiky[den] || {}, [kosiky, den]);
  const [chcemOdber, setChcemOdber] = useState(false);
  const [ziadamDonasku, setZiadamDonasku] = useState(false);
  const [balenie, setBalenie] = useState("NA_MIESTE");
  const [adresaId, setAdresaId] = useState("");
  const [poznamka, setPoznamka] = useState("");
  const [profil, setProfil] = useState(null);
  // Rozpisane spresnenie adresy -- `id` hovori, ku ktorej adrese patri, takze
  // po prepnuti adresy sa ukaze jej ulozene spresnenie, nie to z predoslej.
  const [rozpisane, setRozpisane] = useState({ id: null, text: "" });
  const odoslaneSpresnenie = useRef(null);
  const [odosiela, setOdosiela] = useState(false);
  const [chyba, setChyba] = useState("");
  const [hotovo, setHotovo] = useState(null);

  /*
   * Súhrn sa na širokej obrazovke lepí hore, ale LEN POKIAĽ sa celý zmestí.
   *
   * Vnútorný `overflow-y: auto` tu bol a bola to zlá odpoveď: systémový
   * posuvník v paneli vyzerá cudzo a človek skroluje v skrolovateľnom paneli
   * vnútri skrolovateľnej stránky. Lepiť vyšší panel sa zase nedá vôbec --
   * jeho spodok aj s tlačidlom by ostal pod spodným okrajom okna a nedalo by
   * sa k nemu dostať žiadnym skrolovaním. Keď je teda panel vyšší než okno,
   * lepenie sa vypne a panel skroluje spolu so stránkou.
   */
  const suhrnRef = useRef(null);
  const [lepive, setLepive] = useState(true);

  // Súhrn sa vykreslí až s načítanou ponukou (predtým sú skoré `return`y),
  // preto pozorovatelia čakajú naň -- s prázdnymi závislosťami by sa spustili
  // raz, keď `suhrnRef` ešte nič nemá, a už nikdy.
  const maSuhrn = Boolean(config?.zapnute);

  useEffect(() => {
    const prvok = suhrnRef.current;
    if (!prvok || typeof ResizeObserver === "undefined") return undefined;
    // 2 × $spacing-md -- rovnaká rezerva, akú má `top` v štýloch.
    const prepocitaj = () => setLepive(prvok.scrollHeight <= window.innerHeight - 32);
    prepocitaj();
    const pozorovatel = new ResizeObserver(prepocitaj);
    pozorovatel.observe(prvok);
    window.addEventListener("resize", prepocitaj);
    return () => {
      pozorovatel.disconnect();
      window.removeEventListener("resize", prepocitaj);
    };
  }, [maSuhrn]);

  /*
   * Na mobile je súhrn až POD celou ponukou, takže „+“ pri jedle nemalo
   * žiadnu viditeľnú odozvu -- o pridaní sa človek dozvedel, až keď zišiel
   * dolu. Preto dolná lišta s počtom a sumou, ktorá k súhrnu zavedie.
   *
   * Lišta zmizne až vtedy, keď je vidno tlačidlo „Objednať“ -- nie hneď so
   * začiatkom súhrnu. Rozpis a voľby doručenia sú dlhé a človek by inak
   * počas ich čítania stratil sumu z očí skôr, než má čím objednať. Tlačidlo
   * vojde do okna POD lištu a lišta ho vtedy uvoľní.
   *
   * Callback ref, nie efekt: pásmo s tlačidlom existuje len pri neprázdnom
   * košíku, takže sa pripája a odpája spolu s ním.
   */
  const [tlacidloVidno, setTlacidloVidno] = useState(false);
  const pozorovatelTlacidla = useRef(null);

  const tlacidloRef = useCallback((prvok) => {
    pozorovatelTlacidla.current?.disconnect();
    pozorovatelTlacidla.current = null;
    if (!prvok || typeof IntersectionObserver === "undefined") {
      setTlacidloVidno(false);
      return;
    }
    const pozorovatel = new IntersectionObserver(([zaznam]) => setTlacidloVidno(zaznam.isIntersecting));
    pozorovatel.observe(prvok);
    pozorovatelTlacidla.current = pozorovatel;
  }, []);

  const naSuhrn = () => {
    const plynulo = !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    suhrnRef.current?.scrollIntoView?.({ behavior: plynulo ? "smooth" : "auto", block: "start" });
  };

  // Kosik z tenantStorage sa berie len pri PRVOM nacitani ponuky. Potom uz
  // plati to, co je na obrazovke (opakovane nacitanie po prihlaseni).
  const kosikObnoveny = useRef(false);

  // Config sa nacitava znova po prihlaseni -- ceny sa vtedy prepocitaju
  // podla cennika zakaznika.
  useEffect(() => {
    let zrusene = false;
    axios.get(ORDER_CONFIG_URL)
      .then(({ data }) => {
        if (zrusene) return;
        const ulozeny = kosikObnoveny.current ? null : getKosik();
        kosikObnoveny.current = true;

        setConfig(data);
        const otvoreny = (datum) => (data.dni || []).find((d) => d.datum === datum && d.objednatelny);
        const zUrl = otvoreny(zvolenyDen);
        const prvyOtvoreny = (data.dni || []).find((d) => d.objednatelny);

        // Ulozeny kosik (obnovenie stranky, navrat z registracie) sa vrati den
        // po dni -- kazdy, kym sa na jeho den da objednat. Otvori sa den, na
        // ktorom clovek skoncil, ibaze odkaz z denneho menu hovori inak.
        const obnovene = {};
        for (const [datum, ks] of Object.entries(ulozeny?.kosiky || {})) {
          if (!otvoreny(datum)) continue;
          const platne = platnyKosik(ks, data, datum);
          if (Object.keys(platne).length) obnovene[datum] = platne;
        }
        const posledny = otvoreny(ulozeny?.den)?.datum;
        setDen((aktualny) => aktualny || zUrl?.datum || posledny || prvyOtvoreny?.datum
          || data.dni?.[0]?.datum || "");

        // Jedlo z odkazu rovno v kosiku -- ale len ked patri do zvoleneho dna
        // a da sa objednat. Inak by sa clovek divil, co mu tam pribudlo.
        const polozka = (data.polozky || []).find((p) => String(p.id) === zvolenaPolozka);
        const denPolozky = polozka && (polozka.serving_date || zUrl?.datum);
        if (polozka && !polozka.nedostupne && denPolozky === zUrl?.datum
          && !obnovene[zUrl.datum]?.[polozka.id]) {
          obnovene[zUrl.datum] = { ...obnovene[zUrl.datum], [polozka.id]: 1 };
        }
        if (Object.keys(obnovene).length) {
          setKosiky((k) => (Object.keys(k).length ? k : obnovene));
        }
        if (ulozeny?.poznamka) setPoznamka((p) => p || String(ulozeny.poznamka).slice(0, 200));
      })
      .catch(() => { if (!zrusene) setChybaNacitania("Ponuku sa nepodarilo načítať."); });
    return () => { zrusene = true; };
  }, [isLoggedIn, zvolenyDen, zvolenaPolozka]);

  // Kosik sa pri kazdej zmene odklada do tenantStorage. Az po nacitani ponuky
  // -- prazdny pociatocny stav by inak ulozeny kosik zmazal skor, nez sa obnovi.
  // Ked uz nie je nic v ziadnom dni, zaznam sa odstrani.
  useEffect(() => {
    if (!config) return;
    setKosik(Object.keys(kosiky).length ? { den, kosiky, poznamka } : null);
  }, [config, den, kosiky, poznamka]);

  useEffect(() => {
    if (!jeZakaznik) { setProfil(null); return undefined; }
    let zrusene = false;
    axios.get(CUSTOMER_PROFILE_URL)
      .then(({ data }) => {
        if (zrusene) return;
        setProfil(data);
        setAdresaId((a) => a || String(data.addresses?.[0]?.id || ""));
      })
      .catch(() => { /* adresu si vypyta az odoslanie */ });
    return () => { zrusene = true; };
  }, [jeZakaznik]);

  const dni = config?.dni || [];
  const vybranyDen = dni.find((d) => d.datum === den) || null;

  /*
   * Uzavierka na dnes. Pri pauze sa nepocita -- obrazovka ma vlastne hlasenie
   * a veta o uzavierke by ho len zahlusila.
   *
   * Ked uzavierka prejde pri otvorenej obrazovke, dnesok sa zavrie hned, nie
   * az po obnoveni stranky: `objednatelny` zo servera je stav v case
   * nacitania.
   */
  const uzavierka = useUzavierka(config?.pozastavene ? null : config?.dni);
  // Nie `uzavierka.dnes` -- pri pauze je uzavierka null, „Dnes“ v pase ale
  // plati aj vtedy. Komponent sa s odpoctom prekresluje kazdych 15 s, takze
  // sa o polnoci prepne sam.
  const dnes = terazVBratislave().datum;
  const jeDnes = (datum) => Boolean(uzavierka) && datum === uzavierka.dnes;
  const dnesVyprsalo = uzavierka?.typ === "po-uzavierke";
  const denOtvoreny = (d) => Boolean(d?.objednatelny) && !(dnesVyprsalo && jeDnes(d.datum));
  const vybranyOtvoreny = denOtvoreny(vybranyDen);
  const dovodZavretia = (d) => (d && !denOtvoreny(d) ? d.dovod || PO_UZAVIERKE : null);
  // Odpocet patri len k dnesku -- pri inom dni by sa cital ako jeho uzavierka.
  const uzavierkaVybranehoDna = vybranyDen && jeDnes(vybranyDen.datum) ? uzavierka : null;

  // Rozhoduje ADRESA (R54): kym jej majitel neprideli rozvozovu trasu, donaska
  // na nu jednoducho nie je moznost. Server to kontroluje znova -- toto je len
  // to, co sa cloveku ponukne.
  const adresy = profil?.addresses || [];
  const vybranaAdresa = adresy.find((a) => String(a.id) === String(adresaId)) || null;
  const maDonasku = vybranaAdresa?.rozvoz === true;
  const spresnenie = rozpisane.id === vybranaAdresa?.id
    ? rozpisane.text
    : vybranaAdresa?.address_note || "";
  const setSpresnenie = (text) => setRozpisane({ id: vybranaAdresa?.id, text });

  /**
   * Ulozi spresnenie k adrese, ak sa zmenilo -- pri opusteni pola a znova
   * pred odoslanim objednavky (klik na "Objednat" pride hned po opusteni
   * pola, preto sa rovnaka hodnota neposiela dvakrat, ale caka sa na prvu).
   */
  const ulozSpresnenie = useCallback(() => {
    const a = vybranaAdresa;
    if (!a) return Promise.resolve();
    const hodnota = spresnenie.trim();

    const posledne = odoslaneSpresnenie.current;
    if (posledne?.id === a.id && posledne.hodnota === hodnota) return posledne.uloha;
    if (hodnota === (a.address_note || "")) return Promise.resolve();

    const uloha = axios.patch(CUSTOMER_ADDRESS_URL(a.id), { address_note: hodnota })
      .then(() => {
        setProfil((p) => p && {
          ...p,
          addresses: p.addresses.map((x) => (x.id === a.id ? { ...x, address_note: hodnota || null } : x)),
        });
      })
      .catch((err) => {
        if (odoslaneSpresnenie.current?.uloha === uloha) odoslaneSpresnenie.current = null;
        throw err;
      });
    odoslaneSpresnenie.current = { id: a.id, hodnota, uloha };
    return uloha;
  }, [vybranaAdresa, spresnenie]);
  const sposob = !maDonasku || chcemOdber ? "PICKUP" : "DELIVERY";

  /*
   * Ziadost o donasku vylucuje otazky okolo odberu.
   *
   * Pytat sa cloveka "zjete to na prevadzke?" a sucasne od neho citat "chcem
   * donasku" si odporuje -- a do poznamky to obsluhe pisalo obe vety vedla
   * seba. Kto o donasku poziada, nevybera balenie ani cas prichodu.
   */
  const ziadaDonasku = !maDonasku && ziadamDonasku;
  const vyberaBalenie = sposob === "PICKUP" && !ziadaDonasku;

  /** Jedna veta do zbaleného prehľadu: ako sa k jedlu dostane a v čom bude. */
  const popisPrevzatia = sposob === "DELIVERY"
    ? "Jedlo vám dovezieme"
    : ["Vyzdvihnete na prevádzke", vyberaBalenie && BALENIE.find((b) => b.value === balenie)?.kratky]
      .filter(Boolean).join(" · ");

  /*
   * Voľby doručenia sú zbalené do prehľadu a rozbalí ich „Upraviť“.
   *
   * Pri adrese, na ktorú donáška ešte dohodnutá nie je, sa rozbalia samy: je
   * to prvá objednávka na ňu, takže spôsob prevzatia ani obaly ešte nikto
   * nevyberal. Keď adresa svoju trasu má, všetko je nastavené z minula a
   * stačí si to prečítať.
   */
  const [upravujem, setUpravujem] = useState(false);
  const idAdresy = vybranaAdresa?.id;
  // Registracia adresu nepyta -- bez nej si zakaznik jedlo vyzdvihne na prevadzke.
  const bezAdresy = Boolean(profil) && adresy.length === 0;
  useEffect(() => {
    // Len rozbaliť, nikdy nezbaliť: zatvoriť polia niekomu pod rukami, keď si
    // práve prepol adresu, by bolo horšie než panel o pár riadkov dlhší.
    // Bez adresy je to rovnako prvý odber -- obaly ešte nikto nevyberal.
    setUpravujem((otvorene) => otvorene || bezAdresy || (Boolean(idAdresy) && !maDonasku));
  }, [idAdresy, maDonasku, bezAdresy]);

  /** Jedla dostupne vo vybranom dni, zoskupene podla typu. */
  const skupiny = useMemo(() => {
    if (!config || !den) return [];
    const ceny = config.ceny?.[den] || {};

    const dostupne = (config.polozky || [])
      .filter((p) => (p.serving_date ? p.serving_date === den : true))
      .map((p) => ({ ...p, cena: ceny[p.id]?.price ?? null, objednatelne: ceny[p.id]?.objednatelne !== false }));

    const podlaTypu = new Map();
    for (const p of dostupne) {
      if (!podlaTypu.has(p.item_type)) podlaTypu.set(p.item_type, []);
      podlaTypu.get(p.item_type).push(p);
    }

    // V skupine podla kodu (A, B, C…; 1, 2, … 10) ako u admina a vodica --
    // API ich vracia v poradi zalozenia, nie podla kodu.
    return [...podlaTypu.entries()]
      .map(([typ, polozky]) => ({
        typ,
        meta: ITEM_TYPE_META[typ],
        polozky: polozky.sort((a, b) => compareMenuCodesNatural(a.menu_code, b.menu_code)),
      }))
      .sort((a, b) => (a.meta?.order ?? 99) - (b.meta?.order ?? 99));
  }, [config, den]);

  // Prepnutie dna kosik nemaze -- kazdy den ma svoj (pozri `kosiky`).
  const zmenDen = (novy) => {
    setDen(novy);
    setChyba("");
  };

  /** Prepise kosik zobrazeneho dna; prazdny sa z `kosiky` odstrani. */
  const nastavPocty = useCallback((zmena) =>
    setKosiky((k) => {
      const { [den]: povodne = {}, ...ostatne } = k;
      const dalsie = zmena(povodne);
      return Object.keys(dalsie).length ? { ...ostatne, [den]: dalsie } : ostatne;
    }), [den]);

  const zmenPocet = (id, hodnota) =>
    nastavPocty((s) => {
      const dalsie = { ...s };
      if (hodnota > 0) dalsie[id] = hodnota;
      else delete dalsie[id];
      return dalsie;
    });

  /** Kusov v kosiku daneho dna -- pre znacku na pase dni. */
  const kusovNaDen = (datum) =>
    Object.values(kosiky[datum] || {}).reduce((s, ks) => s + ks, 0);

  /**
   * Nahlad ceny podla rovnakeho pravidla ako server: polievka sparovana
   * s hlavnym jedlom je v cene, samostatna stoji priemer cien polievok.
   * Zavazna je vzdy cena zo servera.
   *
   * Vracia aj ROZPIS -- zakaznik musi pred odoslanim vidiet, co si vlastne
   * vybral. Samotny sucet mu nepovie, ci sa nepomylil v poctoch.
   *
   * Polievka v cene je v rozpise s hlavnym jedlom ZGRUPENA do menu a namiesto
   * ceny ma „v cene“ -- inak by rozpis a sucet nesedeli. Parovanie je len na
   * zobrazenie (polievky a jedla po poradi v ponuke), server dostane iba
   * zoznam jedal. Samostatna polievka ukazuje priemer ako na serveri, aby
   * riadky dali presne sucet.
   */
  const { suma, kusov, mamHlavne, mamPolievku, vybrane } = useMemo(() => {
    let spolu = 0;
    let polievkySpolu = 0;
    const polievky = []; const hlavne = []; const ostatne = [];

    for (const skupina of skupiny) {
      for (const p of skupina.polozky) {
        const ks = pocty[p.id] || 0;
        if (ks <= 0 || p.cena == null) continue;
        spolu += ks;
        const riadok = { id: p.id, nazov: p.name, kod: p.menu_code, ks, cenaKs: Number(p.cena) };
        if (p.item_type === "SOUP") { polievky.push(riadok); polievkySpolu += riadok.cenaKs * ks; }
        else if (p.item_type === "MENU") hlavne.push(riadok);
        else ostatne.push(riadok);
      }
    }

    const polievokKs = polievky.reduce((s, r) => s + r.ks, 0);
    const priemer = polievokKs > 0 ? polievkySpolu / polievokKs : 0;

    // Parovanie po kusoch: kym su polievky aj hlavne jedla, ide z nich menu --
    // polievka „v cene“ a pod nou hlavne jedlo so svojou cenou.
    // `kluc`, nie `id`: ta ista polievka moze byt v dvoch menu aj samostatne.
    // `cena` menu je cena jeho hlavneho jedla -- z nej sa scitava suma.
    const menu = [];
    const zvysokPolievok = polievky.map((r) => ({ ...r }));
    const zvysokHlavnych = hlavne.map((r) => ({ ...r }));
    let i = 0; let j = 0;
    while (i < zvysokPolievok.length && j < zvysokHlavnych.length) {
      const polievka = zvysokPolievok[i]; const jedlo = zvysokHlavnych[j];
      const ks = Math.min(polievka.ks, jedlo.ks);
      menu.push({
        kluc: `menu-${polievka.id}-${jedlo.id}`,
        cena: jedlo.cenaKs * ks,
        menu: [
          { kluc: "p", nazov: polievka.nazov, kod: polievka.kod, ks, vCene: true },
          { kluc: "h", nazov: jedlo.nazov, kod: jedlo.kod, ks, cena: jedlo.cenaKs * ks },
        ],
      });
      polievka.ks -= ks; jedlo.ks -= ks;
      if (polievka.ks === 0) i += 1;
      if (jedlo.ks === 0) j += 1;
    }

    const samostatne = (zoznam, cenaKs) => zoznam
      .filter((r) => r.ks > 0)
      .map((r) => ({ kluc: String(r.id), nazov: r.nazov, kod: r.kod, ks: r.ks, cena: cenaKs(r) * r.ks }));
    const riadky = [
      ...menu,
      ...samostatne(zvysokPolievok, () => priemer),
      ...samostatne(zvysokHlavnych, (r) => r.cenaKs),
      ...samostatne(ostatne, (r) => r.cenaKs),
    ];

    return {
      suma: riadky.reduce((s, r) => s + r.cena, 0),
      kusov: spolu,
      mamHlavne: hlavne.length > 0,
      mamPolievku: polievky.length > 0,
      vybrane: riadky,
    };
  }, [skupiny, pocty]);

  const odosli = useCallback(async () => {
    // Neprihlaseneho rovno prihlasovacie okno nad kosikom (`?prihlasenie` ho
    // otvori v hlavicke) -- kto ucet nema, ma v nom odkaz na registraciu.
    // Kosik medzitym caka v tenantStorage.
    if (!isLoggedIn) { navigate({ search: "?prihlasenie" }); return; }
    // Prihlasena obsluha "Objednat" v menu nevidi, ale na adresu sa dostat vie.
    if (!jeZakaznik) { setChyba("Objednávať môže len zákaznícky účet."); return; }
    if (kusov === 0) { setChyba("Zatiaľ ste si nič nevybrali."); return; }
    // Bez nacitaneho profilu nevieme, ci adresu ma -- odoslat "bez adresy"
    // by komu ju ma, potichu zmenilo donasku na odber.
    if (!profil) { setChyba("Váš profil sa ešte nenačítal. Skúste to, prosím, o chvíľu."); return; }
    // Adresu potrebuje len donaska. Bez adresy je sposob vzdy odber (maDonasku = false).
    if (adresy.length > 0 && !adresaId) { setChyba("Vyberte adresu."); return; }

    setOdosiela(true);
    setChyba("");
    try {
      await ulozSpresnenie();
      const { data } = await axios.post(CUSTOMER_ORDERS_URL, {
        delivery_date: den,
        customer_address_id: adresaId ? Number(adresaId) : undefined,
        delivery_type: sposob,
        // Kto ziada donasku, balenie nevyberal -- server ho od neho ani nechce.
        balenie: vyberaBalenie ? balenie : undefined,
        // Ma zmysel len pri adrese, na ktoru donaska zatial dohodnuta nie je.
        ziadam_donasku: ziadaDonasku ? true : undefined,
        dispatcher_note: poznamka.trim() || undefined,
        items: Object.entries(pocty).map(([id, quantity]) => ({
          daily_menu_id: Number(id), quantity,
        })),
      });
      setHotovo(data);
      // Len kosik odoslaneho dna -- ostatne dni cakaju na svoje objednavky.
      nastavPocty(() => ({}));
      setPoznamka("");
    } catch (err) {
      setChyba(err?.response?.data?.message || "Objednávku sa nepodarilo odoslať.");
    } finally {
      setOdosiela(false);
    }
  }, [isLoggedIn, jeZakaznik, kusov, profil, adresy.length, adresaId, den, sposob, ziadaDonasku, vyberaBalenie, balenie,
    poznamka, pocty, navigate, ulozSpresnenie, nastavPocty]);

  if (chybaNacitania) {
    return <main className="objednavka objednavka--sprava"><p>{chybaNacitania}</p></main>;
  }

  if (!config) {
    return (
      <main className="objednavka objednavka--sprava">
        <AutickoLoader text="Načítavam ponuku…" />
      </main>
    );
  }

  if (!config.zapnute) {
    return (
      <main className="objednavka objednavka--sprava">
        <ShoppingBag size={38} aria-hidden="true" />
        <h1>Online objednávky zatiaľ nie sú spustené</h1>
        <p>Jedlo si u nás objednáte telefonicky.</p>
        <Link to="/kontakt" className="objednavka__odkaz">Kontakt a čas objednávok</Link>
      </main>
    );
  }

  /*
   * Potvrdenie je MODÁL nad obrazovkou, nie samostatná stránka.
   *
   * Celá biela plocha s jednou vetou uprostred pôsobila, akoby sa človek
   * niekam preklikol — a hlavne ho vytrhla z ponuky, do ktorej sa väčšinou
   * chce vrátiť (doobjednať na ďalší deň). Takto za modálom vidí, kde ostal.
   */
  const potvrdenie = (
    <Modal
      isOpen={hotovo !== null}
      onClose={() => setHotovo(null)}
      className="objednavka__potvrdenie"
      footer={
        <>
          <Button variant="ghost" onClick={() => setHotovo(null)}>Objednať ešte niečo</Button>
          <Link to="/moje-objednavky"><Button variant="primary">Moje objednávky</Button></Link>
        </>
      }
    >
      {/* Bez nadpisu: autíčko prejde zľava doprava a za ním sa odkrýva
          správa zo servera (pri doplnení iná než pri novej objednávke).
          Modál sa pri každom otvorení pripája nanovo, takže scéna beží
          zakaždým. Text je v DOM celý od začiatku, čítačka ho prečíta hneď. */}
      <div className="objednavka__hotovo">
        <p className="objednavka__hotovo-text">{hotovo?.message}</p>
        <Auticko rezim="prejazd" sirka={92} className="objednavka__hotovo-auto" />
      </div>
    </Modal>
  );

  /*
   * SÚHRN má štyri pásma oddelené linkou cez celú šírku panela:
   * ČO (rozpis) → ZA KOĽKO (súčet) → AKO (adresa, spôsob, balenie, poznámka)
   * → POTVRDENIE (tlačidlo).
   *
   * Predtým stálo všetko pod sebou s rovnakou medzerou a každá voľba mala
   * vlastný sivý box, takže upozornenie „nemáte adresu“ vyzeralo presne ako
   * obyčajný výber balenia. Sivý podklad teraz nesú UŽ LEN hlásenia; voľby sú
   * holý obsah v pásme a od rozpisu ich delí linka, nie ďalší rámček.
   */
  const suhrn = (
    <aside
      ref={suhrnRef}
      className={["objednavka__suhrn", lepive ? "objednavka__suhrn--lepive" : ""]
        .filter(Boolean).join(" ")}
    >
      {/*
       * Hlavička má obrátenú hierarchiu: „Vaša objednávka“ je len menovka --
       * že to je košík, vidno aj bez nej. Veľkým je DEŇ, na ktorý sa objednáva.
       * Pás dní je hore na stránke a pri odskrolovanej ponuke panel sám o sebe
       * nepovedal, na kedy to vlastne je; pritom jedlá sa menia každý deň a
       * objednať na zlý deň je tu najdrahšia chyba.
       */}
      <div className="objednavka__suhrn-hlavicka">
        <h2 className="objednavka__suhrn-nadpis">
          <span className="objednavka__suhrn-menovka">Vaša objednávka</span>
          {vybranyDen && (
            <span className="objednavka__suhrn-den">
              {popisDna(vybranyDen.datum, dnes).nazov}
              <em>{popisDna(vybranyDen.datum, dnes).cislo}</em>
            </span>
          )}
        </h2>
        {kusov > 0 && (
          <span className="objednavka__suhrn-pocet">{kusov} {pocetSlovom(kusov)}</span>
        )}
      </div>

      {uzavierkaVybranehoDna && (
        <div className="objednavka__suhrn-uzavierka">
          <UzavierkaObjednavok stav={uzavierkaVybranehoDna} />
        </div>
      )}

      {kusov === 0 ? (
        <p className="objednavka__suhrn-prazdny">
          Vyberte si z ponuky.
        </p>
      ) : (
        <>
          <div className="objednavka__pasmo">
            <ul className="objednavka__rozpis">
              {vybrane.map((r) => (r.menu ? (
                // Polievka a jedlo v jednom rámčeku so štítkom: bez neho by
                // „v cene“ nehovorilo, v cene ČOHO tá polievka je.
                <li key={r.kluc} className="objednavka__rozpis-menu">
                  <span className="objednavka__rozpis-menu-stitok">Menu</span>
                  <ul className="objednavka__rozpis">
                    {r.menu.map((m) => <RiadokRozpisu key={m.kluc} riadok={m} />)}
                  </ul>
                </li>
              ) : (
                <RiadokRozpisu key={r.kluc} riadok={r} />
              )))}
            </ul>

            {/* Upozornenie na chybajucu polievku je len informativne (R32) --
                neblokuje odoslanie, len pripomenie, ze polievka je v cene menu.
                Stoji pri rozpise, lebo sa tyka vyberu, nie platby. */}
            {mamHlavne && !mamPolievku && (
              <p className="objednavka__tip">
                <Info size={16} aria-hidden="true" /> K hlavnému jedlu máte polievku v cene.
              </p>
            )}
          </div>

          <div className="objednavka__sucet">
            <span>Spolu</span>
            <strong>{eur(suma)}</strong>
          </div>

          <div className="objednavka__pasmo">
            {/*
             * DORUČENIE: zbalený prehľad namiesto piatich polí pod sebou.
             *
             * Rozbalené to bolo 600 px v 340 px širokom stĺpci, teda panel na
             * dve obrazovky a posuvník v ňom. Pritom pri každej ďalšej
             * objednávke je adresa aj spôsob prevzatia ten istý ako minule --
             * netreba ich vypĺňať, stačí vidieť, čo platí. Nič sa neskrýva:
             * všetky nastavené hodnoty stoja v prehľade ako veta a „Upraviť“
             * ich vymení za polia.
             */}
            {jeZakaznik && profil && (
              <div className="objednavka__dorucenie">
                <div className="objednavka__dorucenie-hlavicka">
                  <span className="objednavka__skupinka-nadpis">Doručenie</span>
                  <button
                    type="button"
                    className="objednavka__upravit"
                    onClick={() => setUpravujem((v) => !v)}
                    aria-expanded={upravujem}
                  >
                    {upravujem ? "Hotovo" : "Upraviť"}
                  </button>
                </div>

                {upravujem ? (
                  <>
                    {/* Adresa je prva: od nej sa odvija, ci je donaska vobec moznost (R54).
                        Vyplna sa aj pri odbere -- objednavka ju ma, len sa na nu nevozi. */}
                    {adresy.length > 0 && (
                      <Select
                        label="Moja adresa"
                        value={adresaId}
                        onChange={(v) => { setAdresaId(v); setChcemOdber(false); setZiadamDonasku(false); }}
                        options={adresy.map((a) => ({ value: String(a.id), label: a.address }))}
                        placeholder="Vyberte adresu"
                        required
                      />
                    )}

                    {/* Spresnenie patri k ADRESE, nie k objednavke -- vo vybere sa za
                        dlhou adresou aj tak neukazalo. Tu sa da rovno opravit a ulozi
                        sa k adrese (ako v profile), takze ho vodic uvidi aj nabuduce. */}
                    {vybranaAdresa && (
                      <label className="objednavka__poznamka">
                        <span>Poznámka k adrese</span>
                        <textarea
                          value={spresnenie}
                          onChange={(e) => setSpresnenie(e.target.value.slice(0, MAX_SPRESNENIE))}
                          onBlur={() => {
                            ulozSpresnenie().catch((err) =>
                              setChyba(err?.response?.data?.message || "Spresnenie adresy sa nepodarilo uložiť."));
                          }}
                          rows={1}
                          maxLength={MAX_SPRESNENIE}
                          placeholder="Napr. 3. poschodie, zvonček Novák"
                        />
                      </label>
                    )}

                    {vybranaAdresa && maDonasku && (
                      <Checkbox
                        className="objednavka__volba"
                        checked={chcemOdber}
                        onChange={setChcemOdber}
                        label={<><Store size={16} aria-hidden="true" /> Tentoraz si prídem osobne na prevádzku</>}
                      />
                    )}

                    {vyberaBalenie && (
                      <div className="objednavka__skupinka">
                        <span className="objednavka__skupinka-nadpis">Ako to máme pripraviť?</span>
                        {BALENIE.map((b) => (
                          <RadioButton
                            key={b.value}
                            className="objednavka__volba"
                            name="balenie"
                            value={b.value}
                            checked={balenie === b.value}
                            onChange={setBalenie}
                            label={b.label}
                          />
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <ul className="objednavka__prehlad">
                    {!bezAdresy && (
                      <li>
                        <MapPin size={15} aria-hidden="true" />
                        <span>{vybranaAdresa?.address || "Vyberte adresu"}</span>
                      </li>
                    )}
                    {spresnenie.trim() && (
                      <li>
                        <StickyNote size={15} aria-hidden="true" />
                        <span>{spresnenie}</span>
                      </li>
                    )}
                    <li>
                      {sposob === "DELIVERY"
                        ? <Truck size={15} aria-hidden="true" />
                        : <Store size={15} aria-hidden="true" />}
                      <span>{popisPrevzatia}</span>
                    </li>
                  </ul>
                )}

                {/* Prva objednavka: donaska este nie je dohodnuta (R56). Zaskrtnutko
                    nic nesubi -- len to prevadzke odkaze v poznamke. Stoji mimo
                    "Upravit", lebo je to ponuka, ktoru clovek este nevidel. */}
                {vybranaAdresa && !maDonasku && (
                  <div className="objednavka__skupinka">
                    <p className="objednavka__hlasenie">
                      <Store size={17} aria-hidden="true" />
                      {/* Jeden span: odstavec je flex a holy text so <strong> by
                          sa inak rozpadli na samostatne stlpce. */}
                      <span>
                        Na túto adresu zatiaľ donášku dohodnutú nemáte, takže si jedlo{" "}
                        <strong>vyzdvihnete na prevádzke</strong>.
                      </span>
                    </p>
                    <Checkbox
                      className="objednavka__volba"
                      checked={ziadamDonasku}
                      onChange={setZiadamDonasku}
                      label="Mám záujem o donášku — prevádzka sa mi ozve"
                    />
                  </div>
                )}

                {/* Registracia adresu NEPYTA -- bez nej sa objednava s odberom na
                    prevadzke. Kto chce donasku, ma rovno odkaz do profilu, kde sa
                    adresa prida aj s mapou. Ziadost o donasku sa bez adresy
                    neponuka: obsluha by ju nemala kam vybavit. */}
                {bezAdresy && (
                  <p className="objednavka__hlasenie">
                    <Store size={17} aria-hidden="true" />
                    <span>
                      Jedlo si <strong>vyzdvihnete na prevádzke</strong>. Ak chcete donášku,
                      pridajte si adresu v <Link to="/profil" className="objednavka__odkaz">profile</Link>.
                    </span>
                  </p>
                )}
              </div>
            )}

            <label className="objednavka__poznamka">
              <span>Poznámka k objednávke</span>
              <textarea
                value={poznamka}
                onChange={(e) => setPoznamka(e.target.value.slice(0, 200))}
                rows={2}
                placeholder={
                  ziadaDonasku
                    ? "Napr. kedy sa vám dá volať"
                    : sposob === "PICKUP"
                      ? "Kedy prídete? napr. o 12:30"
                      : "Napr. zemiaky namiesto hranoliek"
                }
              />
            </label>
          </div>

          <div ref={tlacidloRef} className="objednavka__pasmo objednavka__pasmo--akcia">
            {chyba && <p className="objednavka__chyba" role="alert">{chyba}</p>}

            <Button
              variant="primary"
              className="objednavka__odoslat"
              onClick={odosli}
              disabled={odosiela || !vybranyOtvoreny || config.pozastavene}
            >
              {/*
                Tlačidlo musí jednoznačne hovoriť, že odoslaním vzniká POVINNOSŤ
                ZAPLATIŤ. Nie je to o tom, kedy peniaze odídu: povinnosť vzniká
                uzatvorením zmluvy, aj keď sa platí až pri prevzatí (rovnako to
                majú dobierkové e-shopy). Formulácia „objednať a zaplatiť pri
                prevzatí“ hovorí oboje naraz — záväzok aj to, kedy sa plní.

                Dva riadky, nie jeden: na úzkom paneli by sa veta aj suma lámali
                náhodne. Takto je dole suma, ktorá je na tlačidle to najdôležitejšie.
              */}
              {odosiela ? "Odosielam..." : jeZakaznik ? (
                <span className="objednavka__odoslat-obsah">
                  <span className="objednavka__odoslat-popis">Objednať a zaplatiť pri prevzatí</span>
                  <span className="objednavka__odoslat-suma">{eur(suma)}</span>
                </span>
              ) : "Prihlásiť sa a objednať"}
            </Button>

            {!jeZakaznik && (
              <p className="objednavka__tip">
                Objednávku odošlete po prihlásení. <Link to="/registracia">Vytvoriť účet</Link>
              </p>
            )}
          </div>
        </>
      )}
    </aside>
  );

  return (
    <main className="objednavka">
      <PageHero
        eyebrow="Denné menu"
        title="Objednajte si"
        zvyraznene="obed"
        lead="Vyberte si deň a jedlá."
      />

      <div className="objednavka__obsah">
        {config.pozastavene && (
          <p className="objednavka__pauza" role="status">
            <Info size={16} aria-hidden="true" />
            Objednávanie je dočasne pozastavené. Skúste to, prosím, neskôr.
          </p>
        )}

        {/* Dni su vodorovny pas -- na mobile sa posuva prstom, na siroko sa zmesti cely.
            Server posiela len dni, ktore ma zmysel ponukat: zatvorene (vikend,
            sviatok) v nom nie su vobec a zvysok konci pri sestom. */}
        <div className="objednavka__dni" role="tablist" aria-label="Deň doručenia">
          {dni.map((d) => {
            const { nazov, skratka, tyzden, cislo } = popisDna(d.datum, dnes);
            const vKosiku = kusovNaDen(d.datum);
            return (
              <button
                key={d.datum}
                type="button"
                role="tab"
                aria-selected={d.datum === den}
                className={[
                  "objednavka__den",
                  d.datum === den ? "objednavka__den--aktivny" : "",
                  denOtvoreny(d) ? "" : "objednavka__den--zavrety",
                ].filter(Boolean).join(" ")}
                onClick={() => zmenDen(d.datum)}
                title={dovodZavretia(d) || undefined}
              >
                <span className="objednavka__den-nazov">{nazov}</span>
                <span className="objednavka__den-skratka">{skratka}</span>
                {tyzden && <span className="objednavka__skryte"> ({tyzden})</span>}
                <span className="objednavka__den-cislo">{cislo}</span>
                {/* Košík sa pri prepnutí dňa nezahodí -- bez značky by však
                    človek nevedel, že na iný deň má niečo rozrobené. */}
                {vKosiku > 0 && (
                  <span className="objednavka__den-kosik">
                    {vKosiku}
                    <span className="objednavka__skryte"> {pocetSlovom(vKosiku)} v košíku</span>
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="objednavka__layout">
          <div className="objednavka__ponuka">
            {/* Prevadzka bez jedineho otvoreneho dna v dohlade -- pas je prazdny
                a bez tejto vety by obrazovka vyzerala, ze sa nedonacitala. */}
            {dni.length === 0 && (
              <p className="objednavka__zavrete objednavka__zavrete--prazdne" role="status">
                <Auticko sirka={80} />
                Teraz nie je otvorený žiadny deň na objednanie.
              </p>
            )}

            {dovodZavretia(vybranyDen) && (
              <p className="objednavka__zavrete" role="status">{dovodZavretia(vybranyDen)}</p>
            )}

            {skupiny.length === 0 && vybranyOtvoreny && (
              <p className="objednavka__zavrete">Na tento deň zatiaľ nie je zverejnené menu.</p>
            )}

            {skupiny.map(({ typ, meta, polozky }) => (
              <section key={typ} className="objednavka__skupina">
                <h2 className="objednavka__skupina-nadpis">{meta?.label || typ}</h2>
                <ul className="objednavka__jedla">
                  {polozky.map((p) => {
                    const nedostupne = p.nedostupne || !p.objednatelne || p.cena == null;
                    const vKosiku = (pocty[p.id] || 0) > 0;
                    const zoznam = alergeny(p.allergens_list);
                    return (
                      <li
                        key={p.id}
                        className={[
                          "objednavka__jedlo",
                          nedostupne ? "objednavka__jedlo--nedostupne" : "",
                          vKosiku ? "objednavka__jedlo--vybrane" : "",
                        ].filter(Boolean).join(" ")}
                      >
                        <div className="objednavka__jedlo-text">
                          <span className="objednavka__jedlo-nazov">
                            {p.menu_code && <em>{p.menu_code}</em>}{" "}
                            <span className="objednavka__jedlo-meno">{p.name}</span>
                          </span>
                          {p.description && <span className="objednavka__jedlo-popis">{p.description}</span>}
                          {/* Len čísla v značkách, názov alergénu je v tooltipe. */}
                          {zoznam.length > 0 && (
                            <div className="objednavka__alergeny">
                              <span aria-hidden="true">Alergény:</span>
                              <ul aria-label="Alergény">
                                {zoznam.map((a) => (
                                  <li key={a} className="objednavka__alergen" title={ALERGEN[a]?.label}>
                                    {a}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {nedostupne && <span className="objednavka__nedostupne">Momentálne nedostupné</span>}
                        </div>
                        <div className="objednavka__jedlo-cena">
                          {p.cena != null && <strong>{eur(p.cena)}</strong>}
                          {!nedostupne && vybranyOtvoreny && (
                            <QuantityInput
                              value={pocty[p.id] || 0}
                              onChange={(v) => zmenPocet(p.id, v)}
                            />
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>

          {suhrn}
        </div>
      </div>

      {/* Len na mobile (na širokej obrazovke ju CSS skryje -- súhrn je tam
          stále vedľa ponuky). Deň je v nej z rovnakého dôvodu ako v hlavičke
          súhrnu: pás dní je pri odskrolovanej ponuke dávno mimo obrazovky. */}
      {kusov > 0 && !tlacidloVidno && (
        <div className="objednavka__lista">
          {/* Záblesk celej lišty pri zmene počtu. Samostatná vrstva s `key`,
              nie animácia na lište -- tá by sa remountom prehrala aj s príchodom. */}
          <span key={`zablesk-${kusov}`} className="objednavka__lista-zablesk" aria-hidden="true" />
          {/* Ikona tiež pulzne (`key`) -- je to prvé, kam oko pri pridaní padne. */}
          <span key={kusov} className="objednavka__lista-ikona" aria-hidden="true">
            <ShoppingCart size={20} />
          </span>
          <div className="objednavka__lista-text" aria-live="polite">
            {vybranyDen && (
              <span className="objednavka__lista-den">
                {popisDna(vybranyDen.datum, dnes).nazov} {popisDna(vybranyDen.datum, dnes).cislo}
                {/* Lišta je na mobile jediné, čo z košíka vidno -- bez odpočtu
                    by sa o uzávierke človek dozvedel až pri tlačidle. */}
                {uzavierkaVybranehoDna?.typ === "otvorene" && (
                  <span className={uzavierkaVybranehoDna.nalehave ? "objednavka__lista-odpocet--nalehave" : undefined}>
                    {" · objednáte ešte "}{formatZostava(uzavierkaVybranehoDna.zostavaMinut)}
                  </span>
                )}
              </span>
            )}
            {/* `key` spustí pri každej zmene počtu krátke pulznutie -- to je
                tá odozva na „+“, ktorá predtým chýbala. */}
            <span key={kusov} className="objednavka__lista-suhrn">
              {kusov} {pocetSlovom(kusov)} · <strong>{eur(suma)}</strong>
            </span>
          </div>
          <Button variant="primary" className="objednavka__lista-tlacidlo" onClick={naSuhrn}>
            Pokračovať <ArrowDown size={16} aria-hidden="true" />
          </Button>
        </div>
      )}

      {potvrdenie}
    </main>
  );
}

export default ObjednavkaPage;
